import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { db } from '../firebase';
import { useConfig } from '../contexts/ConfigContext';
import LoadingAnimation from './LoadingAnimation';
import Footer from './Footer';

const formatEventDate = (start, end) => {
  if (!start) return '';
  const s = start.toDate ? start.toDate() : new Date(start);
  if (!end) {
    return s.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  }
  const e = end.toDate ? end.toDate() : new Date(end);
  const sameMonth = s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear();
  if (sameMonth) {
    return `${s.toLocaleDateString('en-US', { month: 'long' })} ${s.getDate()} – ${e.getDate()}, ${s.getFullYear()}`;
  }
  const sameYear = s.getFullYear() === e.getFullYear();
  const startFmt = s.toLocaleDateString('en-US', { month: 'long', day: 'numeric', ...(!sameYear && { year: 'numeric' }) });
  return `${startFmt} – ${e.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`;
};

const formatShortDate = (timestamp) => {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
};

const Event = () => {
  const { id } = useParams();
  const config = useConfig();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminBanner, setAdminBanner] = useState('');

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      const admin = !!user && config.adminEmails.includes(user.email);
      setIsAdmin(admin);
    });
    return () => unsubscribe();
  }, [config.adminEmails]);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const snap = await getDoc(doc(db, 'events', id));
        if (!snap.exists()) {
          setNotFound(true);
        } else {
          const data = { id: snap.id, ...snap.data() };
          setEvent(data);

          const status = data.status || 'published';
          const auth = getAuth();
          const user = auth.currentUser;
          const admin = !!user && config.adminEmails.includes(user.email);

          if (status === 'deleted') {
            if (!admin) { setNotFound(true); return; }
            setAdminBanner('This event is deleted and not visible to the public.');
          } else if (status === 'draft') {
            if (!admin) { setNotFound(true); return; }
            setAdminBanner('This event is a draft and only visible to admins.');
          } else if (status === 'scheduled' && data.scheduled_at) {
            const scheduledAt = data.scheduled_at.toDate ? data.scheduled_at.toDate() : new Date(data.scheduled_at);
            if (scheduledAt > new Date()) {
              if (!admin) { setNotFound(true); return; }
              setAdminBanner('This event is scheduled and not yet live — only visible to admins.');
            }
          }
        }
      } catch (err) {
        console.error('Error fetching event:', err);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    if (config.configLoaded) fetchEvent();
  }, [id, config.configLoaded, config.adminEmails]);

  if (loading) return <div className="stt-page"><LoadingAnimation /></div>;

  if (notFound) {
    return (
      <div className="stt-page">
        <div className="stt-content">
          <div className="stt-section" style={{ textAlign: 'center' }}>
            <p style={{ color: '#888' }}>Event not found.</p>
            <Link to="/events" className="stt-see-all-link">← All Events</Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="stt-page">
      <div className="stt-content">
        <div className="stt-section stt-event-detail">

          {/* Admin-only banner */}
          {adminBanner && (
            <div className="stt-event-admin-banner">{adminBanner}</div>
          )}

          {/* Passed banner */}
          {event.date && event.date.toDate() < new Date() && (
            <div className="stt-event-passed-banner">This event has passed.</div>
          )}

          {/* Title — most prominent */}
          <h1 className="stt-event-detail-title">{event.title}</h1>

          {/* Date */}
          <p className="stt-event-detail-date">{formatEventDate(event.date, event.end_date)}</p>

          <hr className="divider-gold" style={{ margin: '16px 0' }} />

          {/* Meta */}
          <div className="stt-event-detail-meta-list">
            {event.location && <p className="stt-event-detail-meta-item">Location: {event.location}</p>}
            {(event.cost || event.fee) && <p className="stt-event-detail-meta-item">Cost: ${event.cost || event.fee}</p>}
            {event.signup_deadline && <p className="stt-event-detail-meta-item">Sign up by {formatShortDate(event.signup_deadline)}</p>}
          </div>

          {/* Details body */}
          {event.details && (
            <p className="stt-event-detail-body">{event.details}</p>
          )}

          {/* Back link at bottom */}
          <div className="stt-event-detail-footer">
            <Link to="/events" className="stt-see-all-link">← All Events</Link>
          </div>

        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Event;
